import React, { useCallback, useEffect, useState } from "react";
import GoogleMapReact from "google-map-react";
import { useTranslation } from "react-i18next";
import { FmText, FmAutocompleteInputManaged } from "..";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import axios from "axios";
import { useIsMobile } from "../../Hooks";
import Config from "../../Configs";
import RoomIcon from "@mui/icons-material/Room";
import { location } from "../../Constants/Location";

import useStyles from "./FmMapInput.style";

const DEFAULT_LAT = location.defaultLAT;
const DEFAULT_LNG = location.defaultLNG;

interface FmMapProps {
    helperText: string;
    error: any;
    setValue: (isoCode: string, lat: number, lng: number) => void;
    label: string;
    value?: string;
    showMobileMap: boolean;
    setShowMobileMap: (show: boolean) => void;
}

const FmMapInput = ({ helperText, error, setValue, label, value, showMobileMap, setShowMobileMap, ...rest }: FmMapProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const isMobile = useIsMobile();
    const [lastSelectedAddress, setLastSelectedAddress] = useState<string>("");
    const [address, setAddress] = useState<string>("");
    const [previouslySelectedValue, setPreviouslySelectedValue] = useState<string | undefined | null>(null);
    const [activeCountryCode, setActiveCountryCode] = useState<string | undefined | null>(null);
    const [coords, setCoords] = useState<{ lat: number; lng: number } | undefined>(undefined);

    const getCountry = (address: any) => {
        return address?.address_components?.find((component: any) => component.types.includes("country"))?.short_name || "";
    };

    const handleLocationChange = useCallback(async (lat, lng, customName?: string) => {
        const response = await axios.get(Config.googlePlacesApiUrl.replace("{0}", lat).replace("{1}", lng).replace("{2}", Config.googlePlacesApiKey));

        if (response?.data?.results && response?.data?.results.length > 0) {
            const addressName = response.data.results[0].formatted_address || customName;
            const country = getCountry(response.data.results[0]);
            setCoords({ lat, lng });
            setLastSelectedAddress(addressName);
            setAddress(addressName);
            setActiveCountryCode(country);
            setValue && setValue(country, lat, lng);
        }
    }, []);

    const handleAddressChange = useCallback(async (address: string, customName?: string) => {
        const response = await geocodeByAddress(address);

        if (response && response.length > 0) {
            const addressName = (response[0].formatted_address || customName) as any;
            const { lat, lng } = await getLatLng(response[0]);
            const country = getCountry(response[0]);
            setCoords({ lat, lng });
            setLastSelectedAddress(addressName);
            setAddress(addressName);
            setValue && setValue(country, lat, lng);
            setActiveCountryCode(country);
        }
    }, []);

    useEffect(() => {
        if (value !== previouslySelectedValue && value !== activeCountryCode) {
            /* Try set initial location based on current location of user */
            if (!value) {
                if (navigator.geolocation) {
                    navigator.geolocation.getCurrentPosition(
                        (position: GeolocationPosition) => handleLocationChange(position?.coords?.latitude, position?.coords.longitude),
                        () => handleLocationChange(DEFAULT_LAT, DEFAULT_LNG)
                    );
                }
            } else {
                handleAddressChange(t(`countries.${value}`));
            }
            setPreviouslySelectedValue(value);
        }
        () => {
            return;
        };
    }, [previouslySelectedValue, value, handleLocationChange, handleAddressChange, activeCountryCode]);

    const handleChange = (address: string) => {
        setAddress(address);
    };

    const AnyReactComponent = ({ icon }: any) => <span>{icon}</span>;

    const handleSelect = (address: any) => {
        setLastSelectedAddress(address?.description);

        if (address) {
            handleAddressChange(address?.description);
        }
    };

    const handleGetLabel = (item: any) => {
        return item.description || item;
    };

    const handleBlur = () => {
        setAddress(lastSelectedAddress);
    };

    const handleOnTheMapClick = () => {
        isMobile && setShowMobileMap && setShowMobileMap(true);
    };

    return (
        <PlacesAutocomplete value={address} onChange={handleChange} onSelect={handleSelect}>
            {({ getInputProps, suggestions }) => {
                const inputProps = getInputProps();
                const inputPropsWithCustomBlur = {
                    ...inputProps,
                    onBlur: (e: React.FocusEvent<Element, Element>) => {
                        inputProps.onBlur && inputProps.onBlur(e);
                        handleBlur();
                    },
                    helperText: helperText,
                    error: error,
                };

                return (
                    <div className={classes.container}>
                        <FmAutocompleteInputManaged
                            {...rest}
                            inputProps={inputPropsWithCustomBlur}
                            label={label}
                            data={suggestions as any}
                            getLabel={handleGetLabel}
                            onSelect={handleSelect}
                        />
                        <div className={classes.helpTextContainer}>
                            <FmText className={classes.helpText}>{t("agriSolution.selectPlotLocationNote")}</FmText>
                        </div>
                        <div className={classes.selectMapTextContainer}>
                            <FmText className={classes.selectMapText}>
                                {`${t("agriSolution.selectArea")} `}
                                <FmText className={classes.selectOnTheMap} onClick={handleOnTheMapClick}>
                                    {t("agriSolution.onTheMap")}
                                </FmText>
                            </FmText>
                        </div>
                        {(!isMobile || showMobileMap) && (
                            <div className={classes.map}>
                                <GoogleMapReact
                                    bootstrapURLKeys={{
                                        key: Config.googlePlacesApiKey,
                                    }}
                                    yesIWantToUseGoogleMapApiInternals
                                    center={{
                                        lat: coords?.lat || DEFAULT_LAT,
                                        lng: coords?.lng || DEFAULT_LNG,
                                    }}
                                    defaultZoom={11}
                                    onClick={({ lat, lng }: any) => handleLocationChange(lat, lng)}
                                >
                                    <AnyReactComponent
                                        lat={coords?.lat || DEFAULT_LAT}
                                        lng={coords?.lng || DEFAULT_LNG}
                                        icon={<RoomIcon className={classes.markerIcon} />}
                                    />
                                </GoogleMapReact>
                            </div>
                        )}
                    </div>
                );
            }}
        </PlacesAutocomplete>
    );
};

export default FmMapInput;
